import type { KkmServerMappedReceiptPlan, MapMorsReceiptPlanInput } from '@/entities/fiscalization';
import type {
  KkmAcquiringResult,
  KkmExecutionResult,
  KkmHandshakeReady,
  KkmKktInfo,
  KkmServerAdapter,
} from '@/shared/api';

import { mapMorsReceiptPlan } from '@/entities/fiscalization';
import {
  extractKkmFiscalDocument,
  failedMarkingCode,
  fiscalKktBlocker,
  graphqlKkmCommandStatus,
  isMarkingRefusal,
  unownedMarkingCodes,
} from '@/entities/fiscalization';
import { debugAdapterIssue } from '@/shared/api';
import { getGraphQLErrorCode } from '@/shared/lib';
import {
  type OrderFiscalBankResultInput,
  type OrderFiscalExecutionFieldsFragment,
  OrderFiscalFailureKind,
  OrderFiscalKkmCommandStatus,
  type OrderFiscalOperationIdentityInput,
  OrderFiscalOperationStatus,
  type OrderFiscalRefundExecutionFieldsFragment,
  type OrderFiscalSettlementResultInput,
  OrderFiscalSettlementResultOutcome,
} from '@shared/api/graphql/gql/graphql';

export const BANK_REFERENCE_FIELDS = ['rrn', 'authorizationCode', 'universalId'] as const;

export function hasBankReferences(result: KkmExecutionResult | null): boolean {
  return BANK_REFERENCE_FIELDS.some((field) => Boolean(result?.acquiring?.[field]));
}

export function toBankEvidenceInput(acquiring: KkmAcquiringResult): OrderFiscalBankResultInput {
  return {
    authorizationCode: acquiring.authorizationCode,
    maskedPan: acquiring.maskedPan,
    rrn: acquiring.rrn,
    terminalTransactionId: acquiring.universalId,
  };
}

/** Реквизиты primary дополняются недостающими из fallback. */
export function mergeAcquiring(
  primary: KkmAcquiringResult | null | undefined,
  fallback: KkmAcquiringResult | null | undefined,
): KkmAcquiringResult {
  return {
    authorizationCode: primary?.authorizationCode ?? fallback?.authorizationCode ?? null,
    maskedPan: primary?.maskedPan ?? fallback?.maskedPan ?? null,
    receiptNumber: primary?.receiptNumber ?? fallback?.receiptNumber ?? null,
    rrn: primary?.rrn ?? fallback?.rrn ?? null,
    universalId: primary?.universalId ?? fallback?.universalId ?? null,
  };
}

/** Сервер доказал, что чек не создан: доступны повтор печати и отмена платежа. */
export function isReceiptRetryAllowed(execution: {
  operation: Pick<OrderFiscalExecutionFieldsFragment['operation'], 'status' | 'failure_kind'>;
}): boolean {
  return (
    execution.operation.status === OrderFiscalOperationStatus.PaymentCapturedUnfiscalized &&
    execution.operation.failure_kind === OrderFiscalFailureKind.KnownPrePrint
  );
}

export function acquiringCommandId(idCommand: string): string {
  return `${idCommand}-b`;
}

export function identity(
  execution: { id_command: string; operation: { id: string }; payload_hash: string },
  kktDeviceId: string,
): OrderFiscalOperationIdentityInput & { kktDeviceId: string } {
  return {
    idCommand: execution.id_command,
    kktDeviceId,
    operationId: execution.operation.id,
    payloadHash: execution.payload_hash,
  };
}

export function requireReadyHandshake(
  handshake: Awaited<ReturnType<Pick<KkmServerAdapter, 'handshake'>['handshake']>>,
): KkmHandshakeReady {
  if (handshake.kind !== 'ready') {
    throw new Error(handshake.instruction);
  }
  return handshake;
}

export function assertLiveKktReady(info: KkmKktInfo, timezone?: string): void {
  const blocker = fiscalKktBlocker(info, undefined, timezone);
  if (blocker !== null) {
    throw new Error(`${blocker.title}. ${blocker.description}`);
  }

  if (info.sessionState === 1) {
    throw new ShiftReadinessError('reopen', 'Смена на кассе была закрыта извне. Смена в Морсе продолжается.');
  }

  if (info.sessionState === 3) {
    throw new ShiftReadinessError(
      'reopen',
      'Смена идёт больше суток. Деньги не списаны — закройте её, откройте новую и повторите.',
    );
  }
}

export function assertExecutionTarget(
  execution: { execution_target: { kkt_device_id: string } },
  handshake: KkmHandshakeReady,
  instruction = 'Подключите кассу операции.',
): void {
  if (execution.execution_target.kkt_device_id !== handshake.kktDevice.idDevice) {
    throw new Error(`К этому компьютеру подключена другая касса. ${instruction}`);
  }
}

/** Статус берётся из ответа устройства, а не из нормализованного `kind`: backend видит, что именно ответила касса. */
export function commandStatus(result: KkmExecutionResult): OrderFiscalKkmCommandStatus {
  return graphqlKkmCommandStatus(result.response.status) ?? OrderFiscalKkmCommandStatus.Error;
}

export function fiscalResultErrorFields(
  result: KkmExecutionResult,
  operationId: string,
): { errorCode: string; errorMessage: string | null } {
  const error = result.kind === 'error' ? result.error : null;
  // Транспортный сбой не даёт vendorCode, но приносит свой код в ответе (`transport_error`
  // из executeKkmSafely). Он информативнее общего device_error: «мы не получили ответ»
  // и «касса ответила отказом» — разные вещи для разбора в журнале операций.
  const errorCode = error?.vendorCode ?? result.response.error;

  if (errorCode == null) {
    debugAdapterIssue('fiscal_result_vendor_code_fallback', {
      command: result.response.command,
      operationId,
      resultKind: result.kind,
    });
  }

  return {
    errorCode: errorCode ?? 'device_error',
    errorMessage: error?.vendorMessage ?? null,
  };
}

export function operatorMessage(reason: string | null | undefined, instruction: string): string {
  const normalizedReason = reason?.trim();

  return normalizedReason === undefined || normalizedReason === '' || normalizedReason === instruction.trim()
    ? instruction
    : `${normalizedReason} ${instruction}`;
}

/**
 * Отказ ККТ по коду маркировки: кассир должен понять, какую позицию убрать.
 * `null` — отказ не про маркировку, сообщение берётся обычным путём.
 */
export function markingRefusalMessage(result: KkmExecutionResult): string | null {
  if (!isMarkingRefusal(result.response)) {
    return null;
  }

  const code = failedMarkingCode(result.response);

  return `Касса отклонила код маркировки${code === null ? '' : ` …${code.slice(-6)}`}. Уберите позицию и пробейте чек заново.`;
}

export function resultOperatorMessage(result: KkmExecutionResult, instruction: string): string {
  return operatorMessage(result.kind === 'error' ? result.error.vendorMessage : null, instruction);
}

export function fiscalizedResult(result: KkmExecutionResult, timezone: string): OrderFiscalSettlementResultInput {
  const status = commandStatus(result);
  const fiscal = extractKkmFiscalDocument(result.response, timezone);
  const unowned = unownedMarkingCodes(result.response);

  if (fiscal === null) {
    return {
      commandStatus: status,
      errorCode: 'malformed_fiscal_result',
      outcome: OrderFiscalSettlementResultOutcome.Ambiguous,
      unownedMarkingCodes: unowned,
    };
  }

  return {
    commandStatus: status,
    fiscal,
    outcome: OrderFiscalSettlementResultOutcome.Fiscalized,
    unownedMarkingCodes: unowned,
  };
}

export function isFiscalResultValidationError(error: unknown): boolean {
  return getGraphQLErrorCode(error) === 'order_fiscalization_result_invalid';
}

export function safeErrorMessage(error: unknown, fallback = 'Не получилось пробить чек. Попробуйте ещё раз.'): string {
  return error instanceof Error ? error.message : fallback;
}

export class ShiftReadinessError extends Error {
  public constructor(
    public readonly action: 'open' | 'reopen',
    message: string,
  ) {
    super(message);
  }
}

export function mapFiscalExecutionReceipt(
  execution: Pick<
    OrderFiscalExecutionFieldsFragment | OrderFiscalRefundExecutionFieldsFragment,
    'id_command' | 'execution_target' | 'receipt_kind' | 'receipt_plan' | 'settled_at'
  >,
  mapper?: (input: MapMorsReceiptPlanInput) => KkmServerMappedReceiptPlan,
): KkmServerMappedReceiptPlan {
  return (mapper ?? mapMorsReceiptPlan)({
    acquiringIdCommand: acquiringCommandId(execution.id_command),
    executionTarget: execution.execution_target,
    idCommand: execution.id_command,
    receiptKind: execution.receipt_kind,
    receiptPlan: execution.receipt_plan,
    settledAt: execution.settled_at,
  });
}
